import { useQuery } from "@tanstack/react-query";
import { hasCustomerId } from "@/lib/api";
import { fetchPurchasedProducts } from "@/lib/api";
import type {
  ShippingAddress,
  SubscriptionDiscount,
} from "./usePurchasedProductDetail";

export type SubscriptionStatus =
  | "active"
  | "archived"
  | "draft"
  | "unlisted"
  | null;
export interface PurchasedProduct {
  id: number;
  variant_id: number | null;
  variant_title: string | null;
  quantity: number;
  title: string;
  status: SubscriptionStatus;
  image_url: string | null;
  favorite_id: number | null;
  is_favorite: boolean;
  last_bought_at?: string;
  price?: string | number;
  url?: string;
  variants_count?: number | null;
  /** Social-proof "X ratings" count (favorites + seed). Backend: GetCustomerPurchasedProductsController `ratings_count`. */
  ratings_count?: number | null;
  is_subscription?: boolean;
  subscription_status?: string | null;
  discount_percentage?: number | null;
  discount_amount?: number | null;
  price_after_discount?: number | null;
  /** Unit price as recorded on the order's line item (preferred over `price` for what the customer actually paid). */
  order_line_item_price?: number | null;
  shopify_shop_domain?: string | null;
  shopify_subscription_id?: number | null;
  shop_domain?: string | null;
  subscription_next_billing_date?: string | null;
  subscription_last_charge_date?: string | null;
  subscription_paused_until?: string | null;
  paused_until?: string | null;
  subscription_order_count?: number | null;
  can_skip_next_order?: boolean | null;
  subscription_max_reschedule_date?: string | null;
  subscription_unit_price?: number | null;
  subscription_one_time_price?: number | null;
  subscription_discounted_price?: number | null;
  subscription_discounts?: SubscriptionDiscount[] | null;
  subscription_billing_interval?: string | null;
  subscription_billing_interval_count?: number | null;
  subscription_cancellation_requested_at?: string | null;
  /** Set once the customer accepts the product-swap save offer (ISO). */
  subscription_swap_requested_at?: string | null;
  /** When the subscription was cancelled (contract `cancelled_at`, ISO). */
  subscription_cancelled_at?: string | null;
  /** Contract `updated_at` (ISO) — fallback for the cancelled-on date when `cancelled_at` is null. */
  subscription_updated_at?: string | null;
  subscription_shipping_address?: ShippingAddress | null;
  /** Per-billing shipping fee for this subscription (Shopify SubscriptionContract.deliveryPrice); 0 when it ships free, null when unknown. Contract-level, so it's identical across every line item of the subscription. */
  subscription_shipping_price?: number | null;
  /** ISO currency code for {@link subscription_shipping_price}. */
  subscription_shipping_currency?: string | null;
  subscription_conversion_fee_paid?: boolean | null;
  /** ISO time the cancel was scheduled to run after the next delivery; null when none. */
  subscription_cancel_scheduled_at?: string | null;
  subscription_is_flexible?: boolean | null;
  is_variant_hidden?: boolean;
}

export interface PurchasedProductsData {
  products: PurchasedProduct[];
  customer?: {
    shopify_customer_id?: string | number;
    email?: string;
    first_name?: string;
    last_name?: string;
    full_name?: string;
  };
}

export function usePurchasedProducts(authVersion = 0) {
  return useQuery({
    queryKey: ["purchased-products", authVersion],
    queryFn: async (): Promise<PurchasedProductsData> => {
      const result = await fetchPurchasedProducts();
      return {
        products: (result.products ?? []) as PurchasedProduct[],
        customer: result.customer,
      };
    },
    enabled: hasCustomerId(),
    staleTime: 1000 * 60 * 2,
  });
}
